// client/src/components/teams/CoachPlayerView.tsx
//
// Modal con vista completa de stats de un jugador — solo para el coach.
// Reutiliza los breakdown components existentes con datos del stats_payload.

import React, { useState, useEffect } from 'react';
import { useT } from '../../lib/i18n';
import { DerivedStats } from '../../utils/pokerStats/types';
import { useBenchmarks } from '../../contexts/BenchmarkContext';
import { RfiBreakdown } from '../stats/RfiBreakdown';
import { VpipBreakdown } from '../stats/VpipBreakdown';
import { CbetTextureBreakdown } from '../stats/CbetTextureBreakdown';
import { CbetTextureMultiBreakdown } from '../stats/CbetTextureMultiBreakdown';
import { FoldToPushBreakdown } from '../stats/FoldToPushBreakdown';
import { PushFoldBreakdown } from '../stats/PushFoldBreakdown';

interface Props {
  email:      string;
  handsCount: number;
  lastSync:   string | null;
  stats:      DerivedStats;
  onClose:    () => void;
}

// ── Mini celda de stat con color-coding ──────────────────────────────────────

function StatCell({ label, value, statKey, unit = '%' }: {
  label: string;
  value: number;
  statKey: string;
  unit?: string;
}) {
  const bm = useBenchmarks();
  const status = value > 0 ? bm.evalStat(statKey, value, 'all') : 'ok';
  const color =
    status === 'critical' ? '#e05252' :
    status === 'warn'     ? '#e0a852' :
    status === 'ok'       ? '#52c2a0' : 'var(--text)';

  return (
    <div style={{ textAlign: 'center', minWidth: '70px' }}>
      <div style={{
        fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '1.5px',
        textTransform: 'uppercase', marginBottom: '0.3rem',
      }}>
        {label}
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color }}>
        {value}{unit}
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────

export function CoachPlayerView({ email, handsCount, lastSync, stats, onClose }: Props) {
  const { t } = useT();
  const [tab, setTab] = useState<'preflop' | 'postflop'>('preflop');

  // Cerrar con Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Styles ────────────────────────────────────────────────────────────────

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 1000, overflowY: 'auto', padding: '2rem 1rem',
  };

  const modal: React.CSSProperties = {
    background: '#0e1320', border: '1px solid #2a3045', borderRadius: '10px',
    width: '100%', maxWidth: '700px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    fontFamily: 'var(--mono)',
  };

  const card: React.CSSProperties = {
    background: '#111520', border: '1px solid #1e2636',
    borderRadius: '8px', padding: '1rem 1.25rem', marginBottom: '0.75rem',
  };

  const sectionTitle = (text: string) => (
    <div style={{
      fontSize: '0.62rem', color: 'var(--muted)', letterSpacing: '2px',
      textTransform: 'uppercase', marginBottom: '0.75rem',
    }}>
      {text}
    </div>
  );

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '0.65rem', background: 'none', border: 'none',
    borderBottom: active ? '2px solid #4a9ae0' : '2px solid transparent',
    color: active ? '#4a9ae0' : 'var(--muted)',
    fontFamily: 'var(--mono)', fontSize: '0.75rem',
    letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer',
    transition: 'color 0.15s',
  });

  // ── Leaks: contar stats fuera del benchmark ───────────────────────────────

  const bm = useBenchmarks();
  const leakKeys: Array<[string, number]> = [
    ['vpip', stats.vpip], ['pfr', stats.pfr], ['threebet', stats.threebet],
    ['foldTo3bet', stats.foldTo3bet], ['cbetFlop', stats.cbetFlop],
    ['cbetTurn', stats.cbetTurn], ['foldToCbetFlop', stats.foldToCbetFlop],
    ['openLimp', stats.openLimp ?? 0],
  ];
  const criticalCount = leakKeys.filter(([k, v]) => v > 0 && bm.evalStat(k, v, 'all') === 'critical').length;
  const warnCount     = leakKeys.filter(([k, v]) => v > 0 && bm.evalStat(k, v, 'all') === 'warn').length;

  const hasPushFoldData = stats.pushFoldByPosition.some(p => p.opp > 0);

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>

        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem', borderBottom: '1px solid #1e2636',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div>
            <div style={{ fontSize: '0.62rem', color: 'var(--muted)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '0.35rem' }}>
              {t('teams.playerStatsSubtitle')}
            </div>
            <div style={{ fontSize: '1rem', color: 'var(--text)', fontWeight: 600 }}>{email}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
              {handsCount.toLocaleString()} {t('teams.playerStatsHands')}
              {lastSync && ` · ${new Date(lastSync).toLocaleDateString()}`}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
            {criticalCount > 0 && (
              <div style={{ fontSize: '0.68rem', color: '#e05252', background: '#e0525218', border: '1px solid #e0525240', borderRadius: '4px', padding: '0.2rem 0.6rem' }}>
                {criticalCount} {t('teams.playerStatsLeakCritical')}
              </div>
            )}
            {warnCount > 0 && (
              <div style={{ fontSize: '0.68rem', color: '#e0a852', background: '#e0a85218', border: '1px solid #e0a85240', borderRadius: '4px', padding: '0.2rem 0.6rem' }}>
                {warnCount} {t('teams.playerStatsLeakWarn')}
              </div>
            )}
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '1.1rem', padding: '0.2rem' }}
              aria-label="Cerrar"
            >✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1e2636' }}>
          {(['preflop', 'postflop'] as const).map(tabKey => (
            <button key={tabKey} style={tabStyle(tab === tabKey)} onClick={() => setTab(tabKey)}>
              {tabKey === 'preflop' ? t('teams.statsTabPreflop') : t('teams.statsTabPostflop')}
            </button>
          ))}
        </div>

        {/* Contenido */}
        <div style={{ padding: '1.25rem 1.5rem' }}>

          {/* ── Preflop ── */}
          {tab === 'preflop' && (
            <>
              {/* Stats escalares */}
              <div style={card}>
                {sectionTitle(t('teams.statsTabPreflop'))}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', justifyContent: 'flex-start' }}>
                  <StatCell label="VPIP"      value={stats.vpip}      statKey="vpip" />
                  <StatCell label="PFR"       value={stats.pfr}       statKey="pfr" />
                  <StatCell label="PFR/VPIP"  value={stats.pfrVpipRatio} statKey="pfr" />
                  <StatCell label="3Bet"      value={stats.threebet}  statKey="threebet" />
                  <StatCell label="Fold/3Bet" value={stats.foldTo3bet} statKey="foldTo3bet" />
                  <StatCell label="ATS"       value={stats.ats}       statKey="ats" />
                  {(stats.openLimp ?? 0) > 0 && (
                    <StatCell label="Open Limp" value={stats.openLimp ?? 0} statKey="openLimp" />
                  )}
                </div>
              </div>

              {/* Fold to 3Bet split allin/no-allin */}
              {(stats.foldTo3betAllinOpp > 0 || stats.foldTo3betNonAllinOpp > 0) && (
                <div style={card}>
                  {sectionTitle('Fold to 3Bet')}
                  <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                    {stats.foldTo3betAllinOpp > 0 && (
                      <StatCell
                        label={t('stats.foldTo3betVsAllin')}
                        value={stats.foldTo3betAllin}
                        statKey="foldTo3betAllin"
                      />
                    )}
                    {stats.foldTo3betNonAllinOpp > 0 && (
                      <StatCell
                        label={t('stats.foldTo3betVsNonAllin')}
                        value={stats.foldTo3betNonAllin}
                        statKey="foldTo3betNonAllin"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* RFI por posición */}
              {stats.rfiTotalOpp > 0 && (
                <div style={card}>
                  {sectionTitle(t('stats.rfi.title') || 'RFI por posición')}
                  <RfiBreakdown rfi={stats.rfiByPosition} activeStack="all" />
                </div>
              )}

              {/* VPIP por posición */}
              {stats.vpipTotalOpp > 0 && (
                <div style={card}>
                  {sectionTitle(t('stats.vpip.title') || 'VPIP por posición')}
                  <VpipBreakdown vpip={stats.vpipByPosition} />
                </div>
              )}

              {/* Push/Fold short stack */}
              {hasPushFoldData && (
                <div style={card}>
                  {sectionTitle(t('stats.pushFold.title'))}
                  <PushFoldBreakdown pushFold={stats.pushFoldByPosition} />
                </div>
              )}
            </>
          )}

          {/* ── Postflop ── */}
          {tab === 'postflop' && (
            <>
              {/* Stats escalares */}
              <div style={card}>
                {sectionTitle(t('teams.statsTabPostflop'))}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', justifyContent: 'flex-start' }}>
                  <StatCell label="CBet Flop"   value={stats.cbetFlop}       statKey="cbetFlop" />
                  <StatCell label="CBet Turn"   value={stats.cbetTurn}       statKey="cbetTurn" />
                  <StatCell label="Fold/CBet"   value={stats.foldToCbetFlop} statKey="foldToCbetFlop" />
                  <StatCell label="WTSD"        value={stats.wtsd}           statKey="wtsd" />
                  <StatCell label="W@SD"        value={stats.wsd}            statKey="wsd" />
                  <StatCell label="WWSF"        value={stats.wwsf}           statKey="wwsf" />
                  <StatCell label="AF" value={stats.af} statKey="af" unit="" />
                </div>
              </div>

              {/* CBet Texture HU */}
              {stats.cbetFlopByTextureTotalOpp > 0 && (
                <div style={card}>
                  {sectionTitle(t('stats.cbetTexture.title') || 'CBet Flop por textura HU')}
                  <CbetTextureBreakdown cbetTexture={stats.cbetFlopByTexture} />
                </div>
              )}

              {/* CBet Texture Multiway */}
              {stats.cbetFlopByTextureMultiTotalOpp > 0 && (
                <div style={card}>
                  {sectionTitle(t('stats.cbetTextureMulti.title') || 'CBet Flop multiway')}
                  <CbetTextureMultiBreakdown cbetTextureMulti={stats.cbetFlopByTextureMulti} />
                </div>
              )}

              {/* Fold to Push by stack */}
              {stats.foldToPushOpp > 0 && (
                <div style={card}>
                  {sectionTitle(t('stats.foldToPush.title') || 'Fold to Push por stack')}
                  <FoldToPushBreakdown data={stats.foldToPushByShoverStack} />
                </div>
              )}

              {/* Donk / Probe */}
              {(stats.donkFlop > 0 || stats.donkTurn > 0 || stats.donkRiver > 0 ||
                stats.probeTotal > 0 || stats.probeTurn > 0 || stats.probeRiver > 0) && (
                <div style={card}>
                  {sectionTitle('Donk / Probe')}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem' }}>
                    {stats.donkFlop > 0   && <StatCell label="Donk Flop"   value={stats.donkFlop}   statKey="donkFlop" />}
                    {stats.donkTurn > 0   && <StatCell label="Donk Turn"   value={stats.donkTurn}   statKey="donkTurn" />}
                    {stats.donkRiver > 0  && <StatCell label="Donk River"  value={stats.donkRiver}  statKey="donkRiver" />}
                    {stats.probeTotal > 0 && <StatCell label="Probe Flop"  value={stats.probeTotal} statKey="probeTotal" />}
                    {stats.probeTurn > 0  && <StatCell label="Probe Turn"  value={stats.probeTurn}  statKey="probeTurn" />}
                    {stats.probeRiver > 0 && <StatCell label="Probe River" value={stats.probeRiver} statKey="probeRiver" />}
                  </div>
                </div>
              )}
            </>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid #1e2636', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" style={{ fontSize: '0.78rem' }} onClick={onClose}>
            {t('teams.playerStatsClose')}
          </button>
        </div>

      </div>
    </div>
  );
}
